import { WorkflowNode } from '@ply-io/ply-front-shared-utilities';

import ErrorHandlingService from '../error-handling-service';
import { isStepGraphAffectedChanges } from '../types';
import { NODE_ERROR_CODES, TEMP_NODE_ID_KEY_PREFIX } from '../workflow-node.const';

/**
 * left these imports here for typing reference
 */
// import {
// 	useAppsActions,
// 	useFeatureFlagsGetters,
// 	useNodesActions,
// 	useSelectedNodeActions,
// 	useSelectedNodeGetters,
// } from '@/store-composables';

type TempWorkflowNode = Omit<WorkflowNode, 'id'> & { id: WorkflowNode['id'] | string };

export const isNodeTemp = (node?: Pick<WorkflowNode | TempWorkflowNode, 'id'>): node is TempWorkflowNode =>
	!!node && node.id.toString().includes(TEMP_NODE_ID_KEY_PREFIX);

/**
 * lazy load the store composable functions, to avoid circular dependencies
 * ***********************************************************
 * DEVELOPMENT: if you want to use typing for development, make sure to import the composables at the top of the file
 */
const requireStoreComposables = () => require('@/store-composables');

const EDIT_NODE_KEYS_HANDLERS: Record<string, () => void> = {
	action: async () => {
		const { useSelectedNodeActions, useFeatureFlagsGetters } = requireStoreComposables();
		const { isAiAutofillEnabled } = useFeatureFlagsGetters();
		const { resetAndHandleAutofill } = useSelectedNodeActions();
		if (isAiAutofillEnabled.value) {
			await resetAndHandleAutofill();
		}
	},
};

type PartialUpdateEditNodeOptions = {
	onFailure: (e: unknown) => void | Promise<void>;
	reloadDynamic: boolean;
};

const DEFAULT_OPTIONS: PartialUpdateEditNodeOptions = {
	onFailure: (e) => {
		throw e;
	},
	reloadDynamic: false,
};

export const partialUpdateEditNode = async (
	payload: Partial<WorkflowNode>,
	options: Partial<PartialUpdateEditNodeOptions> = DEFAULT_OPTIONS
) => {
	const onFailure = options.onFailure ?? DEFAULT_OPTIONS.onFailure;
	const reloadDynamic = options.reloadDynamic ?? DEFAULT_OPTIONS.reloadDynamic;
	const { useNodesActions, useSelectedNodeActions, useSelectedNodeGetters, useAppsActions } = requireStoreComposables();
	const { editNode } = useSelectedNodeGetters();
	const {
		setIsNodeDataLoading,
		syncEditNodeWithNodeData,
		setEditNode,
		calcAndGetExtraFields,
		updateSideBarState,
		setForceLoadDynamicFields,
	} = useSelectedNodeActions();
	const { updateNode } = useNodesActions();
	const { getActionsOfApp } = useAppsActions();
	if (!editNode.value) {
		throw new Error('No edit node to update');
	}
	if (payload.credentials_configuration) {
		payload.credentials_configuration = { ...editNode.value.credentials_configuration, ...payload.credentials_configuration };
	}
	try {
		await setIsNodeDataLoading(true);
		await setForceLoadDynamicFields(reloadDynamic);
		const isTempNode = isNodeTemp(editNode.value); // saving isTempNode before actual update/creation of node
		const ret = await updateNode(editNode.value.id, payload, true);
		const promises = [];
		if (isTempNode) {
			if (!isStepGraphAffectedChanges(ret)) {
				throw new Error('Expected a StepGraphAffectedChanges object');
			}
			const editNodeAfterCreation = ret.nodes.created.toSorted((nodeA, nodeB) => nodeA.id - nodeB.id)[0];
			// override previous edit node, we have a new id
			await setEditNode(editNodeAfterCreation);
			promises.push(getActionsOfApp(editNodeAfterCreation.service, editNodeAfterCreation.service_version));
		} else {
			if (isStepGraphAffectedChanges(ret)) {
				throw new Error('Expected a WorkflowNode object');
			}
			promises.push(getActionsOfApp(ret.service, ret.service_version));
		}

		promises.push(syncEditNodeWithNodeData());
		await Promise.all(promises);
		await Promise.all(
			Object.keys(payload)
				.map((key) => EDIT_NODE_KEYS_HANDLERS[key]?.())
				.filter(Boolean)
		);
		await Promise.all([calcAndGetExtraFields(true), updateSideBarState()]);
	} catch (e: unknown) {
		if (ErrorHandlingService.extractErrorCode(e) === NODE_ERROR_CODES.FLOW_REACHED_MAXIMUM_NODES_ALLOWED) {
			alert('The feature have reached the maximum number of nodes allowed.');
		}
		const promises = [onFailure(e)];
		if (reloadDynamic) {
			promises.push(setForceLoadDynamicFields(false));
		}
		await Promise.all(promises);
		throw e;
	} finally {
		await setIsNodeDataLoading(false);
	}
};
